import {
  Badge, Box, Button, Flashbar, Header,
  Pagination, CollectionPreferences, SpaceBetween, Table, TextFilter,
  Multiselect, ColumnLayout,
} from "@cloudscape-design/components";
import type { MultiselectProps } from "@cloudscape-design/components";
import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { getAuthToken } from "./auth";
import { apiFetch } from "./apiFetch";
import StatCards from "./StatCards";
import ProjectFormModal from "./ProjectFormModal";
import DeleteConfirmModal from "./DeleteConfirmModal";
import { useDocumentTitle } from "./useDocumentTitle";

interface Project {
  documentId: string;
  name: string;
  accountName: string;
  accountDocumentId: string | null;
  environmentCount: number;
  stackCount: number;
  publishedAt: string | null;
}

interface LinkRow {
  title: string;
  url: string;
}

type EpicRow = LinkRow;

// Persisted shape of the Projects list filters. Only the three facet/search
// values are stored — never page/pageSize/sortDir.
interface SavedFilters {
  q?: string;
  stakeholders?: string[];
  groups?: string[];
}

// Single localStorage key holding the last-used filters as JSON. localStorage
// (not sessionStorage) so filters survive across browser sessions — the user
// "coming back later" is the primary use case.
const FILTERS_KEY = "itpmng.projects.filters";

// Read saved filters. All access is wrapped so a corrupt payload, disabled
// storage (private mode), or quota error silently degrades to "no saved
// filters" and never crashes the page. Also normalises the id arrays so only
// non-empty strings survive.
function loadSavedFilters(): SavedFilters {
  try {
    const raw = localStorage.getItem(FILTERS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as SavedFilters;
    if (!parsed || typeof parsed !== "object") return {};
    const clean = (arr: unknown): string[] =>
      Array.isArray(arr) ? arr.filter((v): v is string => typeof v === "string" && v.length > 0) : [];
    return {
      q: typeof parsed.q === "string" ? parsed.q : undefined,
      stakeholders: clean(parsed.stakeholders),
      groups: clean(parsed.groups),
    };
  } catch {
    return {};
  }
}

// Write the current filters. When nothing is active, remove the key so an
// explicit "clear all" is also remembered as "no filter". Any storage error is
// swallowed (feature degrades to URL-only behaviour).
function saveFilters(f: { q: string; stakeholders: string[]; groups: string[] }): void {
  try {
    const hasAny = f.q.length > 0 || f.stakeholders.length > 0 || f.groups.length > 0;
    if (!hasAny) {
      localStorage.removeItem(FILTERS_KEY);
      return;
    }
    const payload: SavedFilters = {};
    if (f.q) payload.q = f.q;
    if (f.stakeholders.length) payload.stakeholders = f.stakeholders;
    if (f.groups.length) payload.groups = f.groups;
    localStorage.setItem(FILTERS_KEY, JSON.stringify(payload));
  } catch {
    // ignore — storage unavailable / quota exceeded
  }
}

export default function ProjectsPage() {
  useDocumentTitle("Projects");
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = Number(searchParams.get("page")) || 1;
  const pageSize = Number(searchParams.get("pageSize")) || 10;
  const sortDir: "asc" | "desc" = searchParams.get("sortDir") === "desc" ? "desc" : "asc";
  // Search term persisted in the URL (`q`). Coerce to string so the controlled
  // TextFilter value is never undefined, and cap length for robustness against a
  // pathological URL. This is the source of truth that drives the server fetch.
  const search = (searchParams.get("q") ?? "").slice(0, 200);
  // Relational facet filters, each a comma-separated list of documentIds in the
  // URL. Parsed into string[] (empties filtered out) so they can drive both the
  // server fetch and the derived Multiselect selections. Derived per render from
  // searchParams so they stay stable, exactly like `search`.
  const parseIds = (raw: string | null): string[] =>
    (raw ?? "").split(",").map((s) => s.trim()).filter(Boolean);
  const stakeholderIds = parseIds(searchParams.get("stakeholders"));
  const groupIds = parseIds(searchParams.get("groups"));

  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pageCount, setPageCount] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedItems, setSelectedItems] = useState<Project[]>([]);
  const [flash, setFlash] = useState<{ type: "success" | "error"; content: string } | null>(null);
  const [modalMode, setModalMode] = useState<"create" | "edit" | null>(null);
  const [deleteVisible, setDeleteVisible] = useState(false);
  // Prefill values for the edit modal, loaded asynchronously before the modal opens.
  const [editInitial, setEditInitial] = useState<{ name: string; description: string; epics: EpicRow[]; sciforma_links: LinkRow[]; external_links: LinkRow[] } | undefined>(undefined);
  const [saving, setSaving] = useState(false);
  // Local, immediately-responsive input value for the TextFilter. Initialised from
  // the URL term and kept in sync when the URL changes (e.g. refresh / back-forward).
  const [searchInput, setSearchInput] = useState(search);
  // Loaded option lists for the facet filters, fetched once on mount. Typed as
  // the readonly Options array CloudScape Multiselect expects.
  const [stakeholderOptions, setStakeholderOptions] = useState<MultiselectProps.Options>([]);
  const [groupOptions, setGroupOptions] = useState<MultiselectProps.Options>([]);
  const [facetsLoading, setFacetsLoading] = useState(true);
  // Incrementing id to ignore out-of-order (stale) fetch responses.
  const fetchSeqRef = useRef(0);
  // Debounce timer for writing the search term into the URL.
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Ensures the mount restore effect writes the URL at most once, so it can
  // never loop against its own setSearchParams or fight the user's later edits.
  const hasRestoredRef = useRef(false);

  const apiKey = getAuthToken();
  const selected = selectedItems[0];

  // Primitive-string keys derived from the facet id arrays. The fetchProjects
  // useCallback depends on these (not the arrays or `.join(",")` expressions): arrays
  // are a new identity each render, and a `.join(",")` call inside the deps array is a
  // complex expression react-hooks/exhaustive-deps can't statically verify. The
  // callback body also rebuilds its id lists from these keys so its only facet deps
  // are these stable primitives.
  const stakeholderIdsKey = stakeholderIds.join(",");
  const groupIdsKey = groupIds.join(",");

  const fetchProjects = useCallback((page: number, size: number) => {
    if (!apiKey) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    const seq = ++fetchSeqRef.current;
    // Only send the name filter for a non-empty, trimmed term. The term is
    // encodeURIComponent-encoded so it can never inject extra query params and
    // stays a parameterized Strapi DB filter server-side.
    const term = search.trim();
    const filterParam = term
      ? `&filters[name][$containsi]=${encodeURIComponent(term)}`
      : "";
    // Relational facets. Within a facet, selected ids are OR-ed via $in using
    // indexed array notation. Across facets, Strapi treats the two separate
    // top-level filters[...] as AND. Every id is encodeURIComponent-encoded so it
    // stays a parameterized DB filter and can never inject extra query params.
    // The id arrays are reconstructed from the primitive key strings so the
    // callback depends only on those stable primitives (exhaustive-deps can
    // statically check them); the keys are the join of the same arrays, so an
    // empty key yields an empty list and behaviour is unchanged.
    const stakeholderParam = (stakeholderIdsKey ? stakeholderIdsKey.split(",") : [])
      .map((id, i) => `&filters[stakeholders][documentId][$in][${i}]=${encodeURIComponent(id)}`)
      .join("");
    const groupParam = (groupIdsKey ? groupIdsKey.split(",") : [])
      .map((id, j) => `&filters[stakeholder_groups][documentId][$in][${j}]=${encodeURIComponent(id)}`)
      .join("");
    apiFetch(
      `/projects?populate[account][fields][0]=Alias&populate[account][fields][1]=accountid&populate[stacks][fields][0]=documentId&populate[environments][fields][0]=documentId&pagination[page]=${page}&pagination[pageSize]=${size}&sort=name:${sortDir}${filterParam}${stakeholderParam}${groupParam}`
    )
      .then((res) => {
        if (!res.ok) throw new Error(`API error: ${res.status}`);
        return res.json();
      })
      .then((json) => {
        // Ignore results from a superseded (stale) request.
        if (seq !== fetchSeqRef.current) return;
        const { pagination } = json.meta;
        setProjects(
          json.data.map((item: any) => ({
            documentId: item.documentId,
            name: item.name,
            accountName: item.account ? `${item.account.Alias} (${item.account.accountid})` : "—",
            accountDocumentId: item.account?.documentId || null,
            environmentCount: item.environments?.length ?? 0,
            stackCount: item.stacks?.length ?? 0,
            publishedAt: item.publishedAt,
          }))
        );
        setPageCount(pagination.pageCount);
        setTotal(pagination.total);
      })
      .catch((err) => {
        if (seq !== fetchSeqRef.current) return;
        setError(err.message);
      })
      .finally(() => {
        if (seq !== fetchSeqRef.current) return;
        setLoading(false);
      });
  }, [apiKey, sortDir, search, stakeholderIdsKey, groupIdsKey]);

  useEffect(() => { fetchProjects(currentPage, pageSize); }, [fetchProjects, currentPage, pageSize]);

  // Keep the responsive input in sync with the URL term (refresh, back/forward).
  useEffect(() => { setSearchInput(search); }, [search]);

  // Debounce writing the typed term into the URL. Changing the term resets to
  // page 1 while preserving pageSize and sortDir. The fetch re-runs because
  // `search` (derived from the URL) is a dependency of fetchProjects.
  const onSearchChange = (value: string) => {
    const next = value.slice(0, 200);
    setSearchInput(next);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const trimmed = next.trim();
      if (trimmed === search.trim()) return;
      setSearchParams(buildParams({ page: "1", q: trimmed }));
    }, 300);
  };

  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  // Load the (small) facet option lists once. documentId is a system field and is
  // returned automatically, so it must NOT be listed in fields[] (doing so causes
  // a 400). On any failure we fall back to empty options and never crash the page.
  useEffect(() => {
    if (!apiKey) { setFacetsLoading(false); return; }
    let cancelled = false;
    setFacetsLoading(true);
    Promise.all([
      apiFetch(`/stakeholders?fields[0]=firstName&fields[1]=lastName&fields[2]=email&pagination[pageSize]=100&sort=firstName:asc`)
        .then((res) => { if (!res.ok) throw new Error(`API error: ${res.status}`); return res.json(); }),
      apiFetch(`/stakeholder-groups?fields[0]=name&pagination[pageSize]=100&sort=name:asc`)
        .then((res) => { if (!res.ok) throw new Error(`API error: ${res.status}`); return res.json(); }),
    ])
      .then(([stakeholderJson, groupJson]) => {
        if (cancelled) return;
        setStakeholderOptions(
          (stakeholderJson.data || []).map((s: any) => ({
            label: `${s.firstName} ${s.lastName} (${s.email})`,
            value: s.documentId,
          }))
        );
        setGroupOptions(
          (groupJson.data || []).map((g: any) => ({ label: g.name, value: g.documentId }))
        );
      })
      .catch(() => { if (!cancelled) { setStakeholderOptions([]); setGroupOptions([]); } })
      .finally(() => { if (!cancelled) setFacetsLoading(false); });
    return () => { cancelled = true; };
  }, [apiKey]);

  // Build the shared URL params object for every list-state write. Conditionally
  // includes the name search and the two facet filters so none are lost when the
  // user searches / sorts / paginates / changes page size. Overrides let a caller
  // set page/sortDir/pageSize/facets for the specific interaction.
  const buildParams = (overrides: {
    page?: string; pageSize?: string; sortDir?: string;
    q?: string; stakeholders?: string[]; groups?: string[];
  } = {}): Record<string, string> => {
    const params: Record<string, string> = {
      page: overrides.page ?? "1",
      pageSize: overrides.pageSize ?? String(pageSize),
      sortDir: overrides.sortDir ?? sortDir,
    };
    const q = overrides.q ?? search.trim();
    if (q) params.q = q;
    const sh = overrides.stakeholders ?? stakeholderIds;
    if (sh.length) params.stakeholders = sh.join(",");
    const gr = overrides.groups ?? groupIds;
    if (gr.length) params.groups = gr.join(",");
    return params;
  };

  // Persistence: restore last-used filters on a bare visit, and keep storage in
  // sync on every filter change.
  //
  // Precedence (runs once per mount via hasRestoredRef):
  //  1. EXPLICIT URL WINS — if the URL already carries any of q/stakeholders/
  //     groups (even a bookmarked/shared filtered link), use it as-is and also
  //     persist those URL filters so "last-used" reflects them. The URL is NOT
  //     overwritten.
  //  2. BARE VISIT RESTORE — if the URL has none of the three filter params,
  //     read saved filters and, if any are non-empty, write them into the URL
  //     (merging with existing page/pageSize/sortDir, resetting page to 1).
  //     After this redirect the normal derived-state + fetch flow applies them.
  useEffect(() => {
    if (hasRestoredRef.current) return;
    hasRestoredRef.current = true;

    const urlHasFilter =
      searchParams.has("q") || searchParams.has("stakeholders") || searchParams.has("groups");

    if (urlHasFilter) {
      // Explicit URL wins: mirror whatever the URL carries into storage.
      saveFilters({ q: search.trim(), stakeholders: stakeholderIds, groups: groupIds });
      return;
    }

    const saved = loadSavedFilters();
    const savedQ = (saved.q ?? "").trim();
    const savedStakeholders = saved.stakeholders ?? [];
    const savedGroups = saved.groups ?? [];
    if (!savedQ && !savedStakeholders.length && !savedGroups.length) return;

    // Restore into the URL; derived state + fetch then auto-fill the controls.
    setSearchParams(
      buildParams({ page: "1", q: savedQ, stakeholders: savedStakeholders, groups: savedGroups }),
      { replace: true }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save on every change. A single effect covers all mutation paths (search box
  // + both multiselects) without touching individual handlers. It is idempotent
  // — writing the same JSON — so it cannot loop against the restore effect.
  useEffect(() => {
    saveFilters({ q: search.trim(), stakeholders: stakeholderIds, groups: groupIds });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, stakeholderIds.join(","), groupIds.join(",")]);

  // Derive the currently-selected Multiselect options by mapping the URL id arrays
  // against the loaded option lists. Stale ids (no matching option) are ignored so
  // we never render a broken token; the raw ids remain in the URL untouched until
  // the user next changes that facet.
  const selectedStakeholders = stakeholderOptions.filter(
    (o) => o.value != null && stakeholderIds.includes(o.value)
  );
  const selectedGroups = groupOptions.filter(
    (o) => o.value != null && groupIds.includes(o.value)
  );

  // Changing a facet resets to page 1 (like changing the search term) while
  // preserving pageSize, sortDir, q, and the OTHER facet's selection.
  const onStakeholdersChange = (selected: ReadonlyArray<MultiselectProps.Option>) => {
    const ids = selected.map((o) => o.value).filter((v): v is string => !!v);
    setSearchParams(buildParams({ page: "1", stakeholders: ids }));
  };
  const onGroupsChange = (selected: ReadonlyArray<MultiselectProps.Option>) => {
    const ids = selected.map((o) => o.value).filter((v): v is string => !!v);
    setSearchParams(buildParams({ page: "1", groups: ids }));
  };

  const openCreate = () => {
    setEditInitial(undefined);
    setModalMode("create");
  };

  const openEdit = () => {
    if (!selected) return;
    // Fetch the single project to prefill description + link lists (kept out of the list query),
    // then open the edit modal once the initial values are ready.
    apiFetch(`/projects/${selected.documentId}?populate[jira_epics][fields][0]=title&populate[jira_epics][fields][1]=url&populate[sciforma_links][fields][0]=title&populate[sciforma_links][fields][1]=url&populate[external_links][fields][0]=title&populate[external_links][fields][1]=url`)
      .then((res) => {
        if (!res.ok) throw new Error(`API error: ${res.status}`);
        return res.json();
      })
      .then((json) => {
        setEditInitial({
          name: selected.name,
          description: json.data?.description || "",
          epics: (json.data?.jira_epics || []).map((e: any) => ({ title: e.title || "", url: e.url || "" })),
          sciforma_links: (json.data?.sciforma_links || []).map((l: any) => ({ title: l.title || "", url: l.url || "" })),
          external_links: (json.data?.external_links || []).map((l: any) => ({ title: l.title || "", url: l.url || "" })),
        });
        setModalMode("edit");
      })
      .catch((e: any) => setFlash({ type: "error", content: `Failed to load project details: ${e.message}` }));
  };

  const handleSave = async (values: { name: string; description: string; jira_epics: EpicRow[]; sciforma_links: LinkRow[]; external_links: LinkRow[] }) => {
    if (!apiKey) return;
    setSaving(true);
    try {
      const isEdit = modalMode === "edit";
      const url = isEdit ? `/projects/${selected.documentId}` : `/projects`;
      const res = await apiFetch(url, {
        method: isEdit ? "PUT" : "POST",
        body: JSON.stringify({
          data: {
            name: values.name,
            description: values.description || null,
            jira_epics: values.jira_epics,
            sciforma_links: values.sciforma_links,
            external_links: values.external_links,
          },
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error?.message || `API error: ${res.status}`);
      }
      setFlash({ type: "success", content: isEdit ? "Project updated" : "Project created" });
      setModalMode(null);
      setSelectedItems([]);
      fetchProjects(currentPage, pageSize);
    } catch (e: any) {
      setFlash({ type: "error", content: e.message });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!apiKey || !selected) return;
    setSaving(true);
    try {
      const res = await apiFetch(`/projects/${selected.documentId}`, {
        method: "DELETE",
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error?.message || `API error: ${res.status}`);
      }
      setFlash({ type: "success", content: "Project deleted" });
      setDeleteVisible(false);
      setSelectedItems([]);
      fetchProjects(currentPage, pageSize);
    } catch (e: any) {
      setFlash({ type: "error", content: e.message });
    } finally {
      setSaving(false);
    }
  };

  const totalEnvironments = projects.reduce((sum, p) => sum + p.environmentCount, 0);
  const totalStacks = projects.reduce((sum, p) => sum + p.stackCount, 0);

  return (
    <SpaceBetween size="l">
      {flash && (
        <Flashbar items={[{ type: flash.type, content: flash.content, dismissible: true, onDismiss: () => setFlash(null) }]} />
      )}
      <StatCards
        items={[
          { label: "Total projects", value: total, variant: "indigo" },
          { label: "Environments (this page)", value: totalEnvironments, variant: "teal" },
          { label: "Stacks (this page)", value: totalStacks, variant: "amber" },
          { label: "Current page", value: `${currentPage} / ${pageCount}`, variant: "slate" },
        ]}
      />
      <Table
        variant="full-page"
        loading={loading}
        items={projects}
        selectionType="single"
        resizableColumns
        wrapLines
        stickyHeader
        selectedItems={selectedItems}
        onSelectionChange={({ detail }) => setSelectedItems(detail.selectedItems as Project[])}
        trackBy="documentId"
        sortingColumn={{ sortingField: "name" }}
        sortingDescending={sortDir === "desc"}
        onSortingChange={({ detail }) => {
          const nextDir = detail.isDescending ? "desc" : "asc";
          setSearchParams(buildParams({ page: "1", sortDir: nextDir }));
        }}
        columnDefinitions={[
          {
            id: "name",
            header: "Name",
            sortingField: "name",
            cell: (item) => (
              <Link to={`/projects/${item.documentId}`} className="project-name-link">
                {item.name}
              </Link>
            ),
          },
          {
            id: "account", header: "Account", cell: (item) => item.accountName },
          {
            id: "environments",
            header: "Environments",
            cell: (item) =>
              item.environmentCount > 0 ? (
                <Badge color="blue">{item.environmentCount}</Badge>
              ) : (
                <Box color="text-body-secondary">—</Box>
              ),
          },
          {
            id: "stacks",
            header: "Stacks",
            cell: (item) =>
              item.stackCount > 0 ? <Badge color="green">{item.stackCount}</Badge> : <Box color="text-body-secondary">—</Box>,
          },
          {
            id: "publishedAt",
            header: "Published At",
            cell: (item) => item.publishedAt ? new Date(item.publishedAt).toLocaleDateString() : "—",
          },
        ]}
        filter={
          <SpaceBetween size="xs">
            <TextFilter
              filteringText={searchInput}
              onChange={({ detail }) => onSearchChange(detail.filteringText)}
              filteringPlaceholder="Search projects by name"
              countText={
                search.trim() || stakeholderIds.length || groupIds.length
                  ? `${total} match${total === 1 ? "" : "es"}`
                  : undefined
              }
            />
            <ColumnLayout columns={2}>
              <Multiselect
                selectedOptions={selectedStakeholders}
                onChange={({ detail }) => onStakeholdersChange(detail.selectedOptions)}
                options={stakeholderOptions}
                placeholder="Filter by stakeholder"
                deselectAriaLabel={(o) => `Remove ${o.label}`}
                statusType={facetsLoading ? "loading" : "finished"}
                loadingText="Loading stakeholders"
                empty="No stakeholders"
                ariaLabel="Stakeholders"
                tokenLimit={3}
              />
              <Multiselect
                selectedOptions={selectedGroups}
                onChange={({ detail }) => onGroupsChange(detail.selectedOptions)}
                options={groupOptions}
                placeholder="Filter by stakeholder group"
                deselectAriaLabel={(o) => `Remove ${o.label}`}
                statusType={facetsLoading ? "loading" : "finished"}
                loadingText="Loading stakeholder groups"
                empty="No stakeholder groups"
                ariaLabel="Stakeholder groups"
                tokenLimit={3}
              />
            </ColumnLayout>
          </SpaceBetween>
        }
        pagination={
          <Pagination
            currentPageIndex={currentPage}
            pagesCount={pageCount}
            onChange={({ detail }) => {
              setSearchParams(buildParams({ page: String(detail.currentPageIndex) }));
            }}
          />
        }
        preferences={
          <CollectionPreferences
            title="Preferences"
            confirmLabel="Confirm"
            cancelLabel="Cancel"
            preferences={{ pageSize }}
            pageSizePreference={{
              title: "Page size",
              options: [
                { value: 10, label: "10 items" },
                { value: 25, label: "25 items" },
                { value: 50, label: "50 items" },
                { value: 100, label: "100 items" },
              ],
            }}
            onConfirm={({ detail }) => {
              setSearchParams(buildParams({ page: "1", pageSize: String(detail.pageSize ?? 10) }));
            }}
          />
        }
        header={
          <Header
            counter={`(${total})`}
            description="Projects group related CloudFormation stacks by environment (Prod, Dev, Qa, Staging, ...) so the same product can be deployed multiple times without name collisions."
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button iconName="refresh" loading={loading} onClick={() => fetchProjects(currentPage, pageSize)} />
                <Button disabled={!selected} onClick={openEdit}>Edit</Button>
                <Button disabled={!selected} onClick={() => setDeleteVisible(true)}>Delete</Button>
                <Button variant="primary" iconName="add-plus" onClick={openCreate}>Create project</Button>
              </SpaceBetween>
            }
          >
            Projects
          </Header>
        }
        empty={
          <SpaceBetween size="m">
            <Box textAlign="center" color={error ? "text-status-error" : "text-body-secondary"}>
              {error ? `Error: ${error}` : !apiKey ? "No API key configured. Go to Settings to add one." : "No projects found"}
            </Box>
          </SpaceBetween>
        }
      />
      <ProjectFormModal
        visible={modalMode !== null}
        mode={modalMode === "edit" ? "edit" : "create"}
        saving={saving}
        initial={modalMode === "edit" ? editInitial : undefined}
        onDismiss={() => setModalMode(null)}
        onSubmit={handleSave}
      />
      <DeleteConfirmModal
        visible={deleteVisible}
        header="Delete project"
        itemName={selected?.name}
        loading={saving}
        onDismiss={() => setDeleteVisible(false)}
        onConfirm={handleDelete}
      />
    </SpaceBetween>
  );
}
